import { useState, useRef } from "react";
import { toBase64 } from "../util/toBase64";
import { errorToast } from "../util/toastNotifications";

export default function DragDrop(props) {
  const {
    onSuccessfulUpload,
    multiple = false,
    accept = "*/*",
    maxSize = 3_000_000,
    uploadedFiles = [],
    setUploadedFiles,
  } = props;
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const inFlightRef = useRef(new Set());
  const dragDepthRef = useRef(0);

  const validateFile = (file) => {
    if (maxSize && file.size > maxSize) {
      errorToast(
        `"${file.name}" is too large. Maximum size is ${(maxSize / 1_000_000).toFixed(0)}MB.`
      );
      return false;
    }

    if (accept !== "*/*") {
      const allowedTypes = accept.split(",").map((type) => type.trim());
      const fileName = file.name.toLowerCase();
      const isAllowed = allowedTypes.some((type) => {
        if (type.startsWith(".")) {
          return fileName.endsWith(type.toLowerCase());
        }
        if (type.endsWith("/*")) {
          return file.type.startsWith(type.slice(0, -1));
        }
        return type === file.type;
      });
      if (!isAllowed) {
        errorToast(`"${file.name}" is not a supported file type.`);
        return false;
      }
    }
    return true;
  };

  const isDuplicate = (file) => {
    const normalizedFileName = file.name.replace(/ /g, "_").toLowerCase();
    const key = `${normalizedFileName}__${file.size}`;

    if (inFlightRef.current.has(key)) return true;

    return uploadedFiles.some((existing) => {
      const existingName = (
        existing.file_name ??
        existing.name ??
        ""
      ).toLowerCase();
      return existingName === normalizedFileName;
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) {
      return;
    }

    processFiles(files);
  };

  const handleChange = (e) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = "";
  };

  const processFiles = (files) => {
    const duplicates = [];
    const toUpload = [];

    for (const file of files) {
      if (isDuplicate(file)) {
        duplicates.push(file.name);
      } else if (validateFile(file)) {
        toUpload.push(file);
      }
    }

    if (duplicates.length > 0) {
      errorToast(`Already uploaded: ${duplicates.join(", ")}`);
    }

    toUpload.forEach(handleUpload);
  };

  const handleUpload = async (file) => {
    const normalizedFileName = file.name.replace(/ /g, "_").toLowerCase();
    const key = `${normalizedFileName}__${file.size}`;
    inFlightRef.current.add(key);

    try {
      await toBase64(file);
      const preview_url = URL.createObjectURL(file);
      await onSuccessfulUpload({ file, preview_url });
    } catch (err) {
      console.error("Upload failed", err);
      errorToast("Upload failed. Please try again.");
    } finally {
      inFlightRef.current.delete(key);
    }
  };

  return (
    <div className="drag-drop-container">
      <div
        className="form-file-upload"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          id="input-file-upload"
          type="file"
          accept={accept}
          className="input-file-upload"
          multiple={multiple}
          onChange={handleChange}
        />

        <label
          className={`label-file-upload ${dragActive ? "drag-active" : ""}`}
          htmlFor="input-file-upload"
        >
          <div className="action-button-wrapper">
            <p>Drag and drop your file here or</p>
            <button
              type="button"
              className="upload-button"
              onClick={() => inputRef.current?.click()}
            >
              Upload a file
            </button>
          </div>
        </label>
      </div>
    </div>
  );
}
